import { useEffect, useMemo } from 'react';
import { atom, bind, sleep, withDataAtom, withErrorAtom, withRetry, isAtom, toAbortError, reatomResource, isDeepEqual, noop, isObject, } from '@reatom/framework';
import { useCtx } from '@reatom/npm-react';
import { onEvent } from '@reatom/web';
import { createObservableFromAtom, isObservable } from '@/js/libs/observable/index.ts';
import { getComponentDebugName } from '@/js/libs/reatom-devtools/index.ts';
import { NotActive } from './notActive.js';
import { parseError } from './parseError.js';
import { useAbortOnUnmount } from './useAbortOnUnmount.js';
import { useRequestContextValue } from './UseRequestContext.js';
export const DEFAULT_QUERY_PARAMS = { page: 0, size: 250, limit: 250 };
export function createFetch(apiMethod, config, _ctx) {
    for (const [name, prop] of Object.entries(config)) {
        if (name !== 'name' && name !== 'keepPreviousData' && !isAtom(prop))
            throw new TypeError(`${name} should be an atom`);
    }
    const { debounce, params, onSuccess, onFail, abortSignal, getRetry, ignoreErrors, keepPreviousData = true, queryDefaults, name = apiMethod.name, } = config;
    const memoParams = atom((ctx, state) => {
        const a = ctx.spy(params);
        const p = isObservable(a) ? ctx.spy(a.__INTERNAL_DO_NOT_USE_ATOM__) : a;
        const paramsValue = isObject(p)
            ? Object.entries(p).reduce((acc, [key, value]) => {
                acc[key] = isObservable(value) ? ctx.spy(value.__INTERNAL_DO_NOT_USE_ATOM__) : value;
                return acc;
            }, {})
            : p;
        return isDeepEqual(state, paramsValue) ? state : paramsValue;
    }, `${name}._memoParams`);
    const dataParamsAtom = atom(undefined, `${name}._dataParamsAtom`);
    const fetchAction = reatomResource(async (ctx) => {
        const p = ctx.spy(memoParams);
        // if (typeof p === 'function') p = await p(ctx);
        // abort error ignored by `withRetry` and `withErrorAtom`
        if (p === NotActive)
            throw toAbortError('not active');
        onEvent(ctx, ctx.get(abortSignal), 'abort', (event) => ctx.controller.abort(event.target?.reason));
        const aDebounce = ctx.get(debounce);
        if (aDebounce)
            await ctx.schedule(() => sleep(aDebounce));
        const response = await ctx.schedule(() => apiMethod({ params: p, signal: ctx.controller.signal, ignoreErrors: ctx.get(ignoreErrors), queryDefaults: ctx.get(queryDefaults) }));
        // Stamp the params this request was issued with (not the current ones) so a superseded
        // response can never be labelled as fresh for params it was not fetched for.
        if (!keepPreviousData)
            ctx.schedule(() => dataParamsAtom(ctx, p));
        try {
            ctx.get(onSuccess)?.(response.data, p);
        }
        catch {
            // nothing. It is needing to ignore all errors in onSuccess
        }
        return response;
    }, name).pipe(withDataAtom(undefined, (_, response) => response.data), withRetry({
        onReject(ctx, e, retries) {
            const error = parseError(e, name);
            if (!error || error.status !== 403) {
                const getRetryFn = ctx.get(getRetry) ?? (() => (retries === 0 ? 1000 : undefined));
                return getRetryFn(ctx.get(fetchAction.errorAtom), retries);
            }
            return undefined;
        },
    }), withErrorAtom((ctx, e) => {
        const error = parseError(e, name);
        // safety call `onFail` before render
        if (error) {
            ctx.schedule(() => {
                const isSuppressed = ctx.get(ignoreErrors)?.includes(error.status);
                if (!isSuppressed) {
                    const isRetrying = ctx.get(fetchAction.retriesAtom) > 0;
                    ctx.get(onFail)?.(error, isRetrying);
                }
            });
        }
        return error;
    }));
    fetchAction.retriesAtom.__reatom.name = fetchAction.retriesAtom.__reatom.name?.replace('retriesAtom', '_retriesAtom');
    fetchAction.pendingAtom.__reatom.name = fetchAction.pendingAtom.__reatom.name?.replace('pendingAtom', '_pendingAtom');
    const isLoadingAtom = atom((ctx, state) => {
        const isActive = ctx.get(memoParams) !== NotActive;
        const isInit = state === undefined;
        const isPending = ctx.spy(fetchAction.pendingAtom) > 0;
        const isRetrying = ctx.spy(fetchAction.retriesAtom) > 0;
        return isActive && (isInit || isPending || isRetrying);
    }, `${name}.isLoadingAtom`);
    // ensure `isInit` state will handled correctly
    fetchAction.onCall((ctx) => ctx.get(isLoadingAtom));
    let dataSource = fetchAction.dataAtom;
    let setData = bind(_ctx, fetchAction.dataAtom);
    if (!keepPreviousData) {
        const freshDataAtom = atom((ctx) => {
            // Spy the dataAtom unconditionally: the subscription topology must not depend on params
            // matching, otherwise a data$-only consumer tears the resource down on every params change.
            const data = ctx.spy(fetchAction.dataAtom);
            return isDeepEqual(ctx.spy(dataParamsAtom), ctx.spy(memoParams)) ? data : undefined;
        }, `${name}._freshDataAtom`);
        dataSource = freshDataAtom;
        setData = bind(_ctx, (ctx, update) => {
            const currentParams = ctx.get(memoParams);
            // Never re-expose data through the gate while the request is inactive.
            if (currentParams === NotActive)
                return;
            // A functional update must not run over hidden stale-params state the consumer cannot see.
            if (typeof update === 'function' && !isDeepEqual(ctx.get(dataParamsAtom), currentParams))
                return;
            ctx.get(() => {
                dataParamsAtom(ctx, currentParams);
                return fetchAction.dataAtom(ctx, update);
            });
        });
    }
    return {
        retry: async () => {
            await fetchAction.retry(_ctx);
        },
        data$: createObservableFromAtom(dataSource),
        pending$: createObservableFromAtom(isLoadingAtom),
        error$: createObservableFromAtom(fetchAction.errorAtom),
        setData,
        fetchAction,
        init: () => _ctx.subscribe(fetchAction, noop),
    };
}
const defaultParameters = {};
export function useRequest(config) {
    const { onFail: onFailFromContext, debounce: debounceFromContext } = useRequestContextValue();
    const { 
    // Api method
    apiMethod, 
    // parameter, which will be passed to apiMethod
    params = defaultParameters, debounce = debounceFromContext, onSuccess, onFail: onFailFromProps, silent = false, getRetry, ignoreErrors, keepPreviousData, queryDefaults = DEFAULT_QUERY_PARAMS, } = config;
    const onFail = silent ? undefined : (onFailFromProps ?? onFailFromContext);
    const ctx = useCtx();
    const abortSignal = useAbortOnUnmount();
    const name = getComponentDebugName(apiMethod.name);
    const { configAtoms, result } = useMemo(() => {
        const createFetchConfig = {
            debounce: atom(debounce),
            getRetry: atom(undefined),
            params: atom(params),
            onSuccess: atom(undefined),
            onFail: atom(undefined),
            abortSignal: atom(abortSignal),
            ignoreErrors: atom(ignoreErrors ?? null),
            keepPreviousData,
            queryDefaults: atom(queryDefaults),
            name,
        };
        const fetchReturn = createFetch(apiMethod, createFetchConfig, ctx);
        return { configAtoms: createFetchConfig, result: fetchReturn };
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const depsToInit = [ctx, result];
    useEffect(() => {
        if (onSuccess) {
            return result.init();
        }
        return noop;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, depsToInit);
    ctx.get(() => {
        const lastParams = ctx.get(configAtoms.params);
        if (!isDeepEqual(lastParams, params)) {
            configAtoms.params(ctx, params);
        }
        if (ctx.get(configAtoms.debounce) !== debounce) {
            configAtoms.debounce(ctx, debounce);
        }
        if (ctx.get(configAtoms.onSuccess) !== onSuccess) {
            configAtoms.onSuccess(ctx, () => onSuccess);
        }
        if (ctx.get(configAtoms.onFail) !== onFail) {
            configAtoms.onFail(ctx, () => onFail);
        }
        if (ctx.get(configAtoms.getRetry) !== getRetry) {
            configAtoms.getRetry(ctx, () => getRetry);
        }
    });
    return result;
}
